import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { TagOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Spin, message, Input, Checkbox, ConfigProvider, List } from 'antd';
import { Tag, TagEntityType, CreateTagRequest } from '../../types/tags';
import {
	tagsOnPartsRestCrudThunksForSupplier,
	associateTagsWithEntity,
} from '../../thunks/tags_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { RootState } from '../../reducers/root_reducer';
import { withUserType } from '../withUserType';
import { stopDefaultEvents } from '../../utils/DataAccessUtils';
import TagDisplay from '../TagDisplay';
import PaginatedReduxList from '../paginated_redux_list/PaginatedReduxList';
import { EmptyState } from '../empty_state/EmptyState';
import { generateRandomColor } from '../../constants/tagConstants';
import { ColorPicker } from '../TagCreation/ColorPicker';
import { PERMISSION_NAMES, isUserAllowedToAccess } from '../../utils/AuthUtils';

type TagSelectorOwnProps = {
	entityType: TagEntityType;
	entityId: number;
	associatedTagIds?: number[];
	onChange?: (selectedTags: Tag[]) => void;
	disabled?: boolean;
	onSave?: (selectedTags: Tag[]) => void;
	refreshTable?: () => void;
};

type TagSelectorProps = TagSelectorOwnProps & {
	tags: Tag[];
	loading: boolean;
	associateTags: (entityId: number, tagIds: number[]) => Promise<any>;
	createTag: (tag: CreateTagRequest) => Promise<Tag>;
	userType: string;
	companyConfig: any;
	currentUser: any;
};

function TagSelector({
	entityType,
	entityId,
	onChange,
	disabled = false,
	onSave,
	refreshTable,
	tags,
	loading,
	associateTags: associateTagsFn,
	createTag: createTagFn,
	userType,
	companyConfig,
	currentUser,
	associatedTagIds,
}: TagSelectorProps): React.ReactElement {
	const [modalOpen, setModalOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [searchFilters, setSearchFilters] = useState({});
	const [draftSelectedTagIds, setDraftSelectedTagIds] = useState<Set<number>>(new Set());
	const [isSaving, setIsSaving] = useState(false);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
	const [newTagColor, setNewTagColor] = useState(generateRandomColor());
	const [isCreatingTag, setIsCreatingTag] = useState(false);

	const entityTagIds = useMemo(() => new Set<number>(associatedTagIds || []), [associatedTagIds]);

	const trimmedSearchText = useMemo(() => searchText.trim(), [searchText]);

	const canModifyParts = useMemo(() => {
		return isUserAllowedToAccess(userType)(
			PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
			companyConfig,
			currentUser.roles
		);
	}, [userType, companyConfig, currentUser.roles]);

	const canCreateTag = useMemo(() => {
		if (!canModifyParts) return false;
		if (trimmedSearchText.length < 3) return false;
		return !tags.some((tag) => tag.label.toLowerCase() === trimmedSearchText.toLowerCase());
	}, [canModifyParts, tags, trimmedSearchText]);

	useEffect(() => {
		if (!modalOpen) return;
		const timer = setTimeout(() => {
			const next = trimmedSearchText ? { search: trimmedSearchText } : {};
			setSearchFilters((prev) => {
				if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
				return next;
			});
		}, 300);
		return () => clearTimeout(timer);
	}, [trimmedSearchText, modalOpen]);

	useEffect(() => {
		if (modalOpen) {
			// Initialize draft with current entity tags
			setDraftSelectedTagIds(new Set(entityTagIds));
		} else {
			// Clear search when modal closes
			setSearchText('');
			setSearchFilters({});
			setIsColorPickerOpen(false);
			setNewTagColor(generateRandomColor());
			setIsCreatingTag(false);
		}
	}, [modalOpen, entityTagIds]);

	const handleToggleTag = useCallback((tag: Tag) => {
		setDraftSelectedTagIds((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(tag.id)) {
				newSet.delete(tag.id);
			} else {
				newSet.add(tag.id);
			}
			return newSet;
		});
	}, []);

	const handleSave = useCallback(async () => {
		try {
			setIsSaving(true);
			const selectedTagsArray = Array.from(draftSelectedTagIds);
			await associateTagsFn(entityId, selectedTagsArray);
			message.success('Tags updated');
			setModalOpen(false);

			const selectedTags = tags.filter((tag) => draftSelectedTagIds.has(tag.id));
			if (onChange) onChange(selectedTags);
			if (onSave) onSave(selectedTags);
			if (refreshTable) refreshTable();
		} catch (error) {
			message.error('Failed to update tags');
			console.error('Error updating tags:', error);
		} finally {
			setIsSaving(false);
		}
	}, [draftSelectedTagIds, associateTagsFn, entityId, tags, onChange, onSave, refreshTable]);

	const handleCancel = useCallback(() => {
		setDraftSelectedTagIds(new Set(entityTagIds));
		setModalOpen(false);
	}, [entityTagIds]);

	const handleCreateTag = useCallback(async () => {
		if (!canCreateTag || isCreatingTag) return;
		const data: CreateTagRequest = {
			label: trimmedSearchText,
			color: newTagColor,
		};
		try {
			setIsCreatingTag(true);
			const createdTag = await createTagFn(data);
			setDraftSelectedTagIds((prev) => {
				const next = new Set(prev);
				next.add(createdTag.id);
				return next;
			});
			message.success('Tag created');
			setIsColorPickerOpen(false);
			setSearchText('');
			setSearchFilters({});
			setNewTagColor(generateRandomColor());
		} catch (error) {
			message.error('Failed to create tag');
			console.error('Error creating tag:', error);
		} finally {
			setIsCreatingTag(false);
		}
	}, [canCreateTag, isCreatingTag, trimmedSearchText, newTagColor, createTagFn]);

	const renderEmptyContent = useCallback(() => {
		if (canCreateTag) {
			return (
				<div
					className=" flex cursor-pointer flex-row items-center gap-x-3 rounded border bg-white p-2 hover:bg-gray-200"
					onClick={handleCreateTag}
				>
					<PlusOutlined size={12} className="h-3 w-3" />
					<span className="bodyregular text-character-primary">
						Create new tag: <span className="font-semibold ">`{trimmedSearchText}`</span>
					</span>
					<div
						onClick={(event) => {
							stopDefaultEvents(event);
						}}
					>
						<ColorPicker
							currentColor={newTagColor}
							onColorChange={(color) => {
								setNewTagColor(color);
								setIsColorPickerOpen(false);
							}}
							onCancel={() => setIsColorPickerOpen(false)}
							visible={canCreateTag && isColorPickerOpen}
							onVisibleChange={(visible) => setIsColorPickerOpen(visible)}
							buttonDisplay={
								<span
									className="inline-block h-3 w-3 rounded-full"
									style={{ backgroundColor: newTagColor }}
								/>
							}
						/>
					</div>
				</div>
			);
		}

		const defaultMessage =
			trimmedSearchText.length >= 3 ? (
				<div className="text-center">
					<div className="h5Medium text-character-primary	">No tags found.</div>
					<div className="bodyregular mt-2 text-character-secondary">
						Check spelling or try a different search term.
					</div>
				</div>
			) : (
				<div className="text-center">
					<div className="h5Medium text-character-primary">No tags found.</div>
					<div className="bodyregular mt-2 text-character-secondary">
						Type at least 3 characters to search.
					</div>
				</div>
			);

		return (
			<div className="mt-4">
				<EmptyState body={defaultMessage} height="auto" />
			</div>
		);
	}, [canCreateTag, handleCreateTag, trimmedSearchText, newTagColor, isColorPickerOpen]);

	return (
		<div onClick={stopDefaultEvents}>
			<Button
				type="link"
				size="small"
				icon={<PlusOutlined />}
				disabled={disabled}
				onClick={() => setModalOpen(true)}
			>
				Add Tags
			</Button>

			<Modal
				title={
					<div className="flex items-center gap-2">
						<TagOutlined />
						<span>Add Tags</span>
					</div>
				}
				visible={modalOpen}
				onCancel={handleCancel}
				footer={[
					<Button key="cancel" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</Button>,
					<Button
						key="save"
						type="primary"
						onClick={handleSave}
						loading={isSaving || isCreatingTag}
						disabled={isSaving}
					>
						Save
					</Button>,
				]}
				width={500}
				destroyOnClose
				closable={false}
				maskClosable={false}
			>
				<div className="">
					<Input
						placeholder="Search tags..."
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						allowClear
					/>
				</div>

				<div className="max-h-96 overflow-y-auto [&_.ant-list-empty-text]:p-0 [&_.ant-list-items]:mt-4">
					{/* @ts-ignore - connected component typing */}
					<ConfigProvider renderEmpty={() => renderEmptyContent()}>
						{/* @ts-ignore - connected component typing */}
						<PaginatedReduxList
							collectionName="tags"
							targetCollectionName={entityType}
							listItemRenderFunction={(item: Tag) => {
								const isSelected = draftSelectedTagIds.has(item.id);
								return (
									<div
										onClick={() => handleToggleTag(item)}
										className="flex cursor-pointer items-center gap-2 rounded p-2 transition-colors hover:bg-gray-100"
									>
										<Checkbox
											checked={isSelected}
											onChange={() => handleToggleTag(item)}
											onClick={stopDefaultEvents}
										/>
										<TagDisplay tag={item} />
									</div>
								);
							}}
							fetchData={tagsOnPartsRestCrudThunksForSupplier.read}
							initialPagination={{ current: 1, pageSize: 10 }}
							initialSorting={{ sort_by: 'label', order: 'asc' }}
							initialFilters={{}}
							additionalParams={modalOpen ? searchFilters : {}}
							itemLayout="horizontal"
						/>
					</ConfigProvider>
				</div>
			</Modal>
		</div>
	);
}

const mapStateToProps = (
	state: RootState,
	ownProps: TagSelectorOwnProps & { userType: string }
) => ({
	tags: getRecordsForTargetCollection(state.tags, ownProps.entityType) || [],
	loading: state.tags[ownProps.entityType] ? state.tags[ownProps.entityType].fetching : false,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (
	dispatch: any,
	ownProps: TagSelectorOwnProps & { userType: string }
) => ({
	associateTags: (entityId: number, tagIds: number[]) =>
		dispatch(associateTagsWithEntity(ownProps.userType)(ownProps.entityType, entityId, tagIds)),
	createTag: (tag: CreateTagRequest) =>
		dispatch(tagsOnPartsRestCrudThunksForSupplier.create(tag, ownProps.entityType)),
});

// @ts-ignore - Type inference issue with HOC composition
export default withUserType(connect(mapStateToProps, mapDispatchToProps)(TagSelector));
